import { React, useEffect, useState, useContext, useRef, useCallback } from "react";
import ManufacturingSectorData from "../../../../Components/ManufacturingSectorData";
import { MyContextProvider } from "../../../../App";
import axios from "axios";
import { API_URL } from "../../../../Constant";
import { GlobalLoading, showLoading } from "react-global-loading";
import {
  shortMonthToFull,
  handleUserLevelAccessPermissions,
  getAllYears,
  validateRolesIIPManufacturing,
  shortMonthToMonthNumber,
} from "../../../../Custom/Custom";
import { ToastContainer, toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import messages from "../../../../assets/Messages.json";
import titlesData from "../../../../titles.json";
import iipTitleData from "../../../../iip.json";
import othersData from "../../../../others.json";

const ADDRESS_MAX_LENGTH = 500;
const clampAddress = (value) => String(value ?? "").slice(0, ADDRESS_MAX_LENGTH);

function ManufacturingSector() {
  const [districtList, setDistrictList] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonth] = useState([]);
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("");
  const [industryCode, setIndustryCode] = useState("");
  const [districtCode, setDistrictCode] = useState("");
  const [yearName, setYearName] = useState("");
  const [allFormData, setAllFormData] = useState("");
  const [monthName, setMonthName] = useState("");
  const [payload, setPayload] = useState([]);
  const [previousMonthData, setPreviousMonthData] = useState([]);
  const [previousMonthPayload, setPreviousMonthPayload] = useState([]);
  const [previousYearMonthData, setPreviousYearMonthData] = useState([]);
  const [previousYearMonthPayload, setPreviousYearMonthPayload] = useState([]);
  const [isDivision, setIsDivision] = useState(false);
  const [buttonAccess, setButtonAccess] = useState({
    userLevel: "",
    roles: "",
    isVisible: false,
  });
  const [factoryData, setFactoryData] = useState(null);
  const [isFACTOUser, setIsFACTOUser] = useState(false);
  const factoPreviousPayloadRef = useRef(null);
  const getAllFormDataRef = useRef(null);
  const isGetAllFormDataLoadingRef = useRef(false);
  const addressRef = useRef(null);
  const token = useContext(MyContextProvider).token;
  const decode_token = jwtDecode(token);
  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const storedLanguage = localStorage.getItem("language");

  useEffect(() => {
    const el = addressRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [address]);

  useEffect(() => {
    const level = handleUserLevelAccessPermissions(decode_token);
    setButtonAccess(level);

    // Check if user is FACTOIIP role and call getFactoryDetails API
    const checkFACTOAndCallAPI = async () => {
      try {
        const roles = decode_token?.id?.ROLE_CODE?.split('|') || [];

        // Check if user has FACTOIIP role
        const isFACTO = roles.includes('FACTOIIP');
        setIsFACTOUser(isFACTO);

        if (isFACTO) {
          showLoading(true);
          try {
            const response = await axios.post(
              `${API_URL}api/iip/getFactoryDetails`,
              {},
              config
            );
            if (response.data && response.data.status && response.data.data && response.data.data.length > 0) {
              const factoryDataResponse = response.data.data[0];

              // Store factory data for later use
              setFactoryData(factoryDataResponse);

              // Map District Code
              if (factoryDataResponse.districtCode) {
                setDistrictCode(factoryDataResponse.districtCode);
              }

              // Set industry list with single entry for FACTO user
              if (factoryDataResponse.industrialUnitCode && factoryDataResponse.industrialUnitName) {
                setIndustryList([
                  {
                    industrialUnitCode: factoryDataResponse.industrialUnitCode,
                    industrialUnitName: factoryDataResponse.industrialUnitName,
                  },
                ]);
                setIndustryCode(factoryDataResponse.industrialUnitCode);
              }

              // Set address directly
              if (factoryDataResponse.addressIndustry) {
                setAddress(clampAddress(factoryDataResponse.addressIndustry));
              }

              // Set status directly
              if (factoryDataResponse.lockStatus) {
                setStatus(factoryDataResponse.lockStatus);
              }
            }
          } catch (error) {
            console.error('Error fetching factory details:', error);
          } finally {
            showLoading(false);
          }
        }
      } catch (error) {
        console.error('Error checking FACTO role:', error);
      }
    };

    //Get all Months data
    const getMonthsList = (e) => {
      showLoading(true);
      axios
        .get(`${API_URL}api/graph/getMonths/2019`, config)
        .then((res) => {
          let monthDataResponse = res.data;
          if (monthDataResponse.status) {
            setMonth(monthDataResponse.data);
            showLoading(false);
          }
        })
        .catch((err) => {
          console.error(err);
          showLoading(false);
        });
    };

    const getYearList = () => {
      setYears(getAllYears());
    };

    const userRole = async () => {
      showLoading(true);
      try {
        const role = await validateRolesIIPManufacturing(decode_token);
        if (role) {
          getYearList();
          getMonthsList();
          if (
            level.userLevel === "DIVISION LEVEL" &&
            level.roles.includes("DIVADM")
          ) {
            getDistrictListByDivision();
            setIsDivision(true);
          } else {
            getIipDistrictList();
          }
        } else {
          alert(
            "You are not an authorised user to capture the data for IIP manufacturing sector."
          );
          navigate("/dashboard");
        }
      } catch (error) {
        console.log(error);
        showLoading(false);
      }
    };

    // Call FACTO API check first
    checkFACTOAndCallAPI();
    userRole();
  }, []);

  // Single API call to get all data (current, previous month, previous year)
  const getAllFormData = useCallback(async (post_data) => {
    // Create a unique key from post_data to check if this request was already made
    const payloadKey = JSON.stringify(post_data);

    // Check if same request is already in progress
    if (isGetAllFormDataLoadingRef.current) {
      return;
    }

    // Check if this exact payload was already fetched
    if (getAllFormDataRef.current === payloadKey) {
      return;
    }

    // Mark as loading and store the payload key
    isGetAllFormDataLoadingRef.current = true;
    getAllFormDataRef.current = payloadKey;

    showLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}api/iip/IIPDetailsformManufacturingSector`,
        post_data,
        config
      );
      const allIndustryData = response.data;

      if (allIndustryData.status) {
        const currentData = allIndustryData.data.iipIndustrial[0];

        // Set current month data
        setAddress(clampAddress(currentData.address));
        setStatus(currentData.status);
        setAllFormData(allIndustryData.data.iipIndustrial);
        setPayload(allIndustryData.data.payload);

        // Map previous month data from response
        if (currentData.previousMonthData && currentData.previousMonthData.items) {
          const prevMeta = currentData.previousMonthData;
          // Convert previousMonthData.items to iipIndustrial format (with previous month header fields)
          const previousMonthFormatted = [{
            ...currentData,
            detailsOfItemsManufactured: prevMeta.items,
            totalMenDays: prevMeta.totalMenDays ?? "",
            totalManuFacturingDays: prevMeta.totalManuFacturingDays ?? "",
            totalProductionValue: prevMeta.totalProductionValue ?? "0.00",
            uploadFile: prevMeta.uploadFile ?? "",
            remarkReason: currentData.remarkReason || "",
            status: currentData.status || "",
            statusFlag: currentData.statusFlag || "",
            messageLog: currentData.messageLog || [],
            allItemsList: currentData.allItemsList || [],
          }];
          setPreviousMonthData(previousMonthFormatted);
          setPreviousMonthPayload(allIndustryData.data.previousMonth || {});
        } else {
          setPreviousMonthData([]);
          setPreviousMonthPayload({});
        }

        // Map previous year data from response
        if (currentData.previousYearData && currentData.previousYearData.items) {
          const prevYearMeta = currentData.previousYearData;
          const previousYearFormatted = [{
            ...currentData,
            detailsOfItemsManufactured: prevYearMeta.items,
            totalMenDays: prevYearMeta.totalMenDays ?? "",
            totalManuFacturingDays: prevYearMeta.totalManuFacturingDays ?? "",
            totalProductionValue: prevYearMeta.totalProductionValue ?? "0.00",
            remarkReason: currentData.remarkReason || "",
            status: currentData.status || "",
            statusFlag: currentData.statusFlag || "",
            messageLog: currentData.messageLog || [],
            allItemsList: currentData.allItemsList || [],
          }];
          setPreviousYearMonthData(previousYearFormatted);
          setPreviousYearMonthPayload(allIndustryData.data.previousYear || {});
        } else {
          setPreviousYearMonthData([]);
          setPreviousYearMonthPayload({});
        }
      }
    } catch (error) {
      console.error(error);
      // Reset ref on error so it can be retried
      getAllFormDataRef.current = null;
    } finally {
      showLoading(false);
      isGetAllFormDataLoadingRef.current = false;
    }
  }, [config]);

  // Load industry list and set industry code when month, year, and district are available
  // Skip for FACTO users as they get data directly from API response
  useEffect(() => {
    const loadIndustryAndSetCode = async () => {
      // Skip if FACTO user (they get data directly from API)
      if (isFACTOUser) {
        return;
      }

      if (factoryData && monthName && yearName && districtCode) {
        try {
          const post_data = {
            month: monthName,
            year: yearName,
            districtCode: districtCode,
          };

          // Load industry list for the district
          await getIndustry(post_data);

          // Set industry code after industry list is loaded
          if (factoryData.industrialUnitCode) {
            // Wait a bit for industry list to be set
            setTimeout(() => {
              setIndustryCode(factoryData.industrialUnitCode);
            }, 300);
          }
        } catch (error) {
          console.error('Error loading industry list:', error);
        }
      }
    };

    loadIndustryAndSetCode();
  }, [factoryData, monthName, yearName, districtCode, isFACTOUser]);

  useEffect(() => {
    if (!isFACTOUser) {
      return;
    }

    if (!monthName || !yearName || !districtCode || !industryCode) {
      return;
    }

    const payloadKey = `${monthName}|${yearName}|${districtCode}|${industryCode}`;
    if (factoPreviousPayloadRef.current === payloadKey) {
      return;
    }

    const basePayload = {
      month: monthName,
      year: yearName,
      districtCode: districtCode,
      industrycode: industryCode,
    };

    // Single API call - all data (current, previous month, previous year) comes in response
    getAllFormData(basePayload);

    factoPreviousPayloadRef.current = payloadKey;
  }, [
    isFACTOUser,
    monthName,
    yearName,
    districtCode,
    industryCode,
    getAllFormData,
  ]);

  // get District By Division ID
  const getDistrictListByDivision = async () => {
    try {
      const response = await axios.post(
        `${API_URL}api/geDistrictListByDivisionId`,
        { division_Id: decode_token.id.USER_LEVEL_LOCATION },
        config
      );
      const allDistrict = response.data;

      if (allDistrict.status) {
        setDistrictList(allDistrict.data);
        showLoading(false);
      }
    } catch (error) {
      console.error(error);
      showLoading(false);
    }
  };

  // All district list; for DISTRICT LEVEL + DISIIP role, show only district matching USER_LEVEL_LOCATION
  const getIipDistrictList = async (post_data) => {
    showLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}api/getallDistrictList`,
        config
      );
      const allDistrict = response.data;

      if (allDistrict.status) {
        let listToSet = allDistrict.data || [];
        const userLocation = (decode_token?.id?.USER_LEVEL_LOCATION ?? '').toString().trim();
        const roleCode = (decode_token?.id?.ROLE_CODE ?? '').toString();
        const userType = (decode_token?.id?.USER_TYPE ?? '').toString();
        const roles = roleCode.split('|').map((r) => r.trim());
        const isDistrictLevelDisiip = userType === 'DISTRICT LEVEL' && roles.includes('DISIIP') && userLocation !== '';
        if (isDistrictLevelDisiip && listToSet.length > 0) {
          listToSet = listToSet.filter(
            (d) => String(d.DISTRICT_CODE ?? '').trim() === userLocation
          );
        }
        setDistrictList(listToSet);
        showLoading(false);
      }
    } catch (error) {
      console.error(error);
      showLoading(false);
    }

    // try {
    //   const response = await axios.post(
    //     `${API_URL}api/iip/getIIPDistrictListByData`,post_data,
    //     config
    //   );
    //   const allDistrict = response.data;
    //    if (allDistrict.status) {
    //      setDistrictList(allDistrict.data.iipResultsDistrict);
    //   }
    // } catch (error) {
    //   console.error(error);
    //  }finally{
    //   showLoading(false);
    // }
  };

  async function getIndustry(post_data) {
    showLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}api/iip/IIPManufacturingSector`,
        post_data,
        config
      );
      const allIndustryData = response.data;
      if (allIndustryData.status) {
        setIndustryList(allIndustryData.data.iipIndustrial);
      }
    } catch (error) {
      console.error(error);
    } finally {
      showLoading(false);
    }
  }


  const handleDropdownChange = (e) => {
    let post_data = "";
    let previous_data = "";
    switch (e.target.name) {
      case "district_name":
        if (monthName === "") {
          toast.error("Please Select Month.", messages.COLOR_RED);
          return false;
        }
        if (yearName === "") {
          toast.error("Please Select Year.", messages.COLOR_RED);
          return false;
        }
        setDistrictCode(e.target.value);
        post_data = {
          districtCode: e.target.value,
        };
        setIndustryCode("");
        setAddress("");
        getIndustry(post_data);
        setAllFormData([]);
        break;
      case "month_name":
        setMonthName(e.target.value);
        if (!isFACTOUser) {
          setYearName("");
          setDistrictCode("");
          setIndustryCode("");
          setAddress("");
          setAllFormData([]);
        }
        break;
      case "year_name":
        let currentYear = new Date().getFullYear();
        let selectedYear = parseInt(e.target.value);

        if (selectedYear > currentYear) {
          toast.error("Invalid Year Selection.", messages.COLOR_RED);
          return false;
        } else if (selectedYear === currentYear) {
          let currentMonth = new Date().getMonth() + 1;
          let selectedMonth = shortMonthToMonthNumber(monthName);
          currentMonth = currentMonth < 10 ? `0${currentMonth}` : currentMonth;
          if (selectedMonth > currentMonth) {
            toast.error("Invalid Month Selection.", messages.COLOR_RED);
            setMonthName("");
            setYearName("");
            return false;
          }
        }

        post_data = {
          month: monthName,
          year: e.target.value,
        };
        setYearName(e.target.value);
        if (!isFACTOUser) {
          setDistrictCode("");
          setIndustryCode("");
          setAddress("");
          setAllFormData([]);
        }
        if (monthName === "") {
          toast.error(messages.MONTH_MSG, messages.COLOR_RED);
          return false;
        }
        // if(monthName && e.target.value !=='' && isDivision === false){
        //   getIipDistrictList(post_data)
        // }
        break;
      case "Industry_name":
        if (districtCode === "") {
          toast.error("Please Select District.", messages.COLOR_RED);
          return false;
        }
        post_data = {
          districtCode: districtCode,
          month: monthName,
          year: yearName,
          industrycode: e.target.value,
        };
        setIndustryCode(e.target.value);
        // Single API call - all data (current, previous month, previous year) comes in response
        getAllFormData(post_data);

        break;
      case "Status":
        setStatus(e.target.value);

        break;
      default:
        console.log("switch case are not match");
        break;
    }
  };

  //Reset Form Data
  const resetFormData = () => {
    // Reset all API call refs to allow fresh API calls after reset
    getAllFormDataRef.current = null;
    isGetAllFormDataLoadingRef.current = false;
    
    if (isFACTOUser) {
      setMonthName("");
      setYearName("");
      setAllFormData([]);
      setPayload([]);
      setPreviousMonthData([]);
      setPreviousMonthPayload([]);
      setPreviousYearMonthData([]);
      setPreviousYearMonthPayload([]);
      setStatus("");
      factoPreviousPayloadRef.current = null;
      return;
    }
    setMonthName("");
    setDistrictCode("");
    setIndustryCode("");
    setAddress("");
    setYearName("");
    setStatus("");
    setAllFormData([]);
    setPreviousMonthData([]);
    setPreviousMonthPayload([]);
    setPreviousYearMonthData([]);
    setPreviousYearMonthPayload([]);
    factoPreviousPayloadRef.current = null;
  };

  const ManufacturingSectorValidation = () => {
    if (monthName === "") {
      toast.error(messages.MONTH_MSG, messages.COLOR_RED);
      return false;
    }

    if (yearName === "") {
      toast.error(messages.YEAR_NAME_MSG, messages.COLOR_RED);
      return false;
    }
    if (districtCode === "") {
      toast.error(messages.DISTRICT_NAME_MSG, messages.COLOR_RED);
      return false;
    }

    if (industryCode === "") {
      toast.error("Please Select Industry", messages.COLOR_RED);
      return false;
    }
    return true;
  };

  const refreshCurrentFormData = useCallback(async () => {
    if (!monthName || !yearName || !districtCode || !industryCode) {
      return;
    }

    // Force refresh even when payload is same as previous call.
    getAllFormDataRef.current = null;

    const post_data = {
      month: monthName,
      year: yearName,
      districtCode: districtCode,
      industrycode: industryCode,
    };

    await getAllFormData(post_data);
  }, [monthName, yearName, districtCode, industryCode, getAllFormData]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer />
      <div className="container-fluid py-3">
        <div className="card mx-3 mb-3">
          <div className="card-body pb-1">
            <div className="row align-items-center">
              <h4 className="page_heading">
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      storedLanguage === "hi"
                        ? titlesData.hi.IIP_MANUFACTURING_SECTOR
                        : titlesData.en.IIP_MANUFACTURING_SECTOR,
                  }}
                />
              </h4>

              <div className="row align-items-center">
                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.MONTH
                      : titlesData.en.MONTH}
                  </label>
                  <select
                    name="month_name"
                    className="form-select"
                    onChange={handleDropdownChange}
                    value={monthName}
                  >
                    <option value="" disabled>
                      Select Month
                    </option>
                    {months &&
                      months.map((ele, i) => (
                        <option key={i} value={ele.MONTH}>
                          {shortMonthToFull(ele.MONTH)}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.YEAR
                      : titlesData.en.YEAR}
                  </label>
                  <select
                    name="year_name"
                    className="form-select"
                    onChange={handleDropdownChange}
                    value={yearName}
                  >
                    <option value="" disabled>
                      Select Year
                    </option>
                    {years &&
                      years.map((ele, index) => (
                        <option key={index} value={ele}>
                          {ele}
                        </option>
                      ))}
                  </select>
                </div>

                {isDivision && (
                  <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                    <label>
                      {storedLanguage === "hi"
                        ? titlesData.hi.DIVISION
                        : titlesData.en.DIVISION}
                    </label>
                    <input
                      typr="text"
                      className="form-control"
                      disabled
                      value={decode_token.id.LOCATION_NAME}
                    />
                  </div>
                )}

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.DISTRICT
                      : titlesData.en.DISTRICT}
                  </label>
                  <select
                    name="district_name"
                    id="district_name"
                    className="form-select"
                    onChange={handleDropdownChange}
                    value={districtCode}
                    disabled={isFACTOUser}
                  >
                    <option value="" disabled>
                      Select District
                    </option>
                    {districtList &&
                      districtList
                        .slice()
                        .sort((a, b) =>
                          a.DISTRICT_CODE.localeCompare(b.DISTRICT_CODE)
                        )
                        .map((ele, index) => (
                          <option key={index} value={ele.DISTRICT_CODE}>
                            {ele.DISTRICT_CODE}-{ele.DISTRICT_NAME}
                          </option>
                        ))}
                  </select>
                </div>

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.NAME_OF_THE_INDUSTRY
                      : titlesData.en.NAME_OF_THE_INDUSTRY}
                  </label>
                  <select
                    name="Industry_name"
                    id="Industry_name"
                    className="form-select"
                    onChange={handleDropdownChange}
                    value={industryCode}
                    disabled={isFACTOUser}
                  >
                    <option value="" disabled>
                      {" "}
                      Select Industry
                    </option>
                    {isFACTOUser && factoryData && factoryData.industrialUnitName ? (
                      <option value={factoryData.industrialUnitCode}>
                        {factoryData.industrialUnitName}
                      </option>
                    ) : (
                      industryList !== "" &&
                      industryList.map((item, index) => (
                        <option value={item.industrialUnitCode} key={index}>
                          {" "}
                          {item.industrialUnitName}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3" style={{ minWidth: 0 }}>
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.ADDRESS
                      : titlesData.en.ADDRESS}
                  </label>
                  <textarea
                    ref={addressRef}
                    className="form-control"
                    name="address"
                    id="address"
                    rows={1}
                    maxLength={ADDRESS_MAX_LENGTH}
                    value={address}
                    readOnly
                    style={{
                      resize: "none",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  />
                </div>

                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? titlesData.hi.STATUS
                      : titlesData.en.STATUS}
                  </label>
                  <select
                    name="Status"
                    id="Status"
                    className="form-select"
                    onChange={handleDropdownChange}
                    value={status}
                  >
                    <option value="" disabled>
                      {" "}
                      Select Status
                    </option>
                    <option value="1"> Open</option>
                    <option value="0"> Close</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mx-3 mb-3">
          {allFormData.length > 0 ? (
            <ManufacturingSectorData
              type="ManufacturingSectorData"
              payload={payload}
              districtCode={districtCode}
              config={config}
              resetFormData={resetFormData}
              userInfoObject={buttonAccess}
              yearName={yearName}
              monthName={monthName}
              allFormData={allFormData}
              status={status}
              address={address}
              industryCode={industryCode}
              industryList={industryList}
              url="/iip/IIPMonthlyProductionManufacturingSectorSaveDB"
              iipTitleData={iipTitleData}
              storedLanguage={storedLanguage}
              othersData={othersData}
              titlesData={titlesData}
              previousMonthData={previousMonthData}
              previousMonthPayload={previousMonthPayload}
              previousYearMonthData={previousYearMonthData}
              previousYearMonthPayload={previousYearMonthPayload}
              onSaveSuccessRefresh={refreshCurrentFormData}
            />
          ) : (
            <div className="card-body">
              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ width: "100%" }}>
                  <div>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.DESCRIPTOIN
                      : iipTitleData.en.DESCRIPTOIN}
                  </div>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.TOTAL_MAN_DAYS
                            : iipTitleData.en.TOTAL_MAN_DAYS}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.TOTAL_MANUFACTURING_DAYS
                            : iipTitleData.en.TOTAL_MANUFACTURING_DAYS}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            name="days1"
                            id="days1"
                            maxLength="10"
                            value=""
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            name="days2"
                            id="days2"
                            maxLength="10"
                            value=""
                            readOnly
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {storedLanguage === "hi"
                ? iipTitleData.hi.DETAILS_OF_ITEMS_MANUFACTURED
                : iipTitleData.en.DETAILS_OF_ITEMS_MANUFACTURED}
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>
                      {storedLanguage === "hi"
                        ? othersData.hi.SERIAL_NO
                        : othersData.en.SERIAL_NO}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? othersData.hi.ITEM_NAME
                        : othersData.en.ITEM_NAME}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? titlesData.hi.UNIT
                        : titlesData.en.UNIT}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.QUANTITY
                        : iipTitleData.en.QUANTITY}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.VALUE_IN_RS
                        : iipTitleData.en.VALUE_IN_RS}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.PREVIOUS_YEAR_VARIATION
                        : iipTitleData.en.PREVIOUS_YEAR_VARIATION}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.PREVIOUS_MONTH_VARIATION
                        : iipTitleData.en.PREVIOUS_MONTH_VARIATION}
                    </th>
                    <th>
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.REASON
                        : iipTitleData.en.REASON}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>
                      <select
                        name="Status"
                        id="Status"
                        className="form-select"
                        value=""
                        disabled
                      >
                        <option value="" disabled>
                          {" "}
                          Select Status
                        </option>
                      </select>
                    </td>

                    <td>
                      <select
                        name="Status"
                        id="Status"
                        className="form-select"
                        value=""
                        readOnly
                      >
                        <option value="" disabled>
                          {" "}
                          Select Status
                        </option>
                      </select>
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        name="days2"
                        id="days2"
                        maxLength="10"
                        value=""
                        readOnly
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        name="days2"
                        id="days2"
                        maxLength="10"
                        value=""
                        readOnly
                      />
                    </td>
                    <td></td>
                    <td></td>
                    <td>
                      <select
                        name="Status"
                        id="Status"
                        className="form-select"
                        value=""
                        readOnly
                      >
                        <option value="0"> [-- Select --] </option>
                        <option value="1">Shut down in factory</option>
                        <option value="2">
                          Decrease / increase in value of goods for the product
                          of production
                        </option>
                        <option value="3">
                          Material not available for produce item
                        </option>
                        <option value="4">
                          Do not produce due to repair of machines
                        </option>
                        <option value="5">Strike in factory</option>
                        <option value="99">Other</option>
                      </select>
                    </td>
                  </tr>

                  <tr>
                    <td colSpan="7">
                      {storedLanguage === "hi"
                        ? iipTitleData.hi.OTHER_MISCELLANEOUS_ITEMS
                        : iipTitleData.en.OTHER_MISCELLANEOUS_ITEMS}
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        name="days2"
                        id="days2"
                        maxLength="10"
                        value=""
                        readOnly
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="row">
                <div className="col-md-6">
                  <label>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.TOTAL_QUANTITY
                      : iipTitleData.en.TOTAL_QUANTITY}
                  </label>
                  <textarea
                    type="text"
                    className="form-control"
                    name="address"
                    id="address"
                    maxLength="12"
                    value=""
                    readOnly
                  />
                </div>

                <div className="col-md-6">
                  <label>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.GRAND_TOTAL
                      : iipTitleData.en.GRAND_TOTAL}
                  </label>
                  <textarea
                    type="text"
                    className="form-control"
                    name="address"
                    id="address"
                    maxLength="12"
                    value=""
                    readOnly
                  />
                </div>
              </div>
              <br />
              {/* <div className="row">
                <div className="col-1 text-center">
                  <label>Remark </label>
                </div>
                <div className="col-11">
                  <textarea
                    type="text"
                    className="form-control"
                    name="remark"
                    id="Remark"
                    maxLength="12"
                    value=""
                    readOnly
                  />
                </div>
              </div> */}

              <div className="row">
                <div className="col-md-6">
                  <label>Remark</label>
                  <textarea
                    type="text"
                    className="form-control"
                    name="remarkReason"
                    id="remarkReason"
                    value=""
                    readOnly
                  />
                </div>
                <div className="col-md-6">
                  <label>Details Sheet (File Upload)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.xls,.xlsx"
                    disabled="true"
                    readOnly
                  />
                  
                  <button
                    type="button"
                    className="btn btn-sm btn-success mt-2"
                    disabled
                  >
                    Download
                  </button>
                </div>
              </div>
              <br />
              <div className="row">
                <div>
                  <div>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.PREVIOUS_MONTH_DATA
                      : iipTitleData.en.PREVIOUS_YEAR_WITH_MONTH_DATA}
                  </div>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>
                          {storedLanguage === "hi"
                            ? othersData.hi.SERIAL_NO
                            : othersData.en.SERIAL_NO}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? othersData.hi.ITEM_NAME
                            : othersData.en.ITEM_NAME}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? titlesData.hi.UNIT
                            : titlesData.en.UNIT}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_YEAR_QUANTITY
                            : iipTitleData.en.PREVIOUS_YEAR_QUANTITY}

                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_YEAR_VALUE_IN_RS
                            : iipTitleData.en.PREVIOUS_YEAR_VALUE_IN_RS}

                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_YEAR_VARIATION
                            : iipTitleData.en.PREVIOUS_YEAR_VARIATION}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_MONTH_QUANTITY
                            : iipTitleData.en.PREVIOUS_MONTH_QUANTITY}

                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_MONTH_VALUE_IN_RS
                            : iipTitleData.en.PREVIOUS_MONTH_VALUE_IN_RS}

                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.PREVIOUS_MONTH_VARIATION
                            : iipTitleData.en.PREVIOUS_MONTH_VARIATION}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* <div style={{ width: "50%" }}>
                  <div>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.PREVIOUS_MONTH_DATA
                      : iipTitleData.en.PREVIOUS_MONTH_DATA}
                  </div>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>
                          {storedLanguage === "hi"
                            ? othersData.hi.SERIAL_NO
                            : othersData.en.SERIAL_NO}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? othersData.hi.ITEM_NAME
                            : othersData.en.ITEM_NAME}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? titlesData.hi.UNIT
                            : titlesData.en.UNIT}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.QUANTITY
                            : iipTitleData.en.QUANTITY}
                        </th>
                        <th>
                          {storedLanguage === "hi"
                            ? iipTitleData.hi.VALUE_IN_RS
                            : iipTitleData.en.VALUE_IN_RS}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div> */}
              </div>

              <div className="row">
                <div className="col-12 text-center pt-3">
                  {
                    <button
                      onClick={(e) => ManufacturingSectorValidation(e, "SAVE")}
                      type="button"
                      className="btn btn_orange mx-1"
                    >
                      Add Row
                    </button>
                  }
                  {
                    <button
                      onClick={(e) => ManufacturingSectorValidation(e, "SAVE")}
                      type="button"
                      className="btn btn_orange mx-1"
                    >
                      Delete Row
                    </button>
                  }
                  {
                    <button
                      onClick={(e) => ManufacturingSectorValidation(e, "SAVE")}
                      type="button"
                      className={`btn btn_orange mx-1`}
                    >
                      Save
                    </button>
                  }

                  {(buttonAccess.userLevel === "DISTRICT LEVEL" ||
                    buttonAccess.userLevel === "DIVISION LEVEL") && (
                      <button
                        onClick={(e) =>
                          ManufacturingSectorValidation(e, "SUBMIT")
                        }
                        type="button"
                        className="btn btn_orange mx-1"
                      >
                        Submit
                      </button>
                    )}

                  {buttonAccess.userLevel === "HEAD QUARTER" && (
                    <>
                      <button
                        onClick={(e) =>
                          ManufacturingSectorValidation(e, "ACCEPT")
                        }
                        type="button"
                        className={`btn btn_orange mx-1`}
                      >
                        Accept
                      </button>
                      <button
                        onClick={(e) =>
                          ManufacturingSectorValidation(e, "REJECT")
                        }
                        type="button"
                        className={`btn btn_orange mx-1`}
                      >
                        Reject
                      </button>
                    </>
                  )}

                  <button
                    type="reset"
                    className="btn btn_orange mx-1"
                    onClick={(e) => ManufacturingSectorValidation(e, "REJECT")}
                  >
                    Reset
                  </button>

                  <button
                    type="button"
                    className="btn btn_orange mx-1"
                    onClick={(e) => navigate("/dashboard")}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={(e) =>
                      ManufacturingSectorValidation(e, "SAVE FOR NEXT MONTH")
                    }
                    type="button"
                    className={`btn btn_orange mx-1`}
                  >
                    Save for next month
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ManufacturingSector;
